import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../../config/api";

function formatTime(seconds) {
    const value = Math.floor(seconds);
    const minutes = Math.floor(value / 60);
    const rest = String(value % 60).padStart(2, "0");
    return minutes < 60 ? `${minutes}:${rest}` :
        `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}:${rest}`;
}

function VideoChapters({ videoId, language }) {
    const { currentUser } = useAuth();
    const [chapters, setChapters] = useState(null);
    const [error, setError] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const t = (en, zh) => (language === "zh" ? zh : en);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        setChapters(null);
        setError(false);
        async function load() {
            try {
                const token = await currentUser.getIdToken();
                const { data } = await axios.get(`${getApiUrl()}/api/video/${videoId}/chapters`, {
                    headers: { Authorization: `Bearer ${token}` }, signal: controller.signal, timeout: 30_000,
                });
                if (data?.result !== "success" || data.source !== "video_agent" ||
                    data.video_id !== videoId || !Array.isArray(data.chapters)) {
                    throw new Error("Invalid chapter response");
                }
                if (active) setChapters(data.chapters);
            } catch (err) {
                if (active) setError(true);
            }
        }
        load();
        return () => { active = false; controller.abort(); };
    }, [currentUser, videoId, refresh]);

    if (error) return <div role="alert" className="text-sm text-red-600">
        {t("Could not load chapters.", "無法載入章節。")}
        <button type="button" onClick={() => setRefresh((value) => value + 1)} className="ml-3 underline">
            {t("Retry", "重試")}
        </button>
    </div>;
    if (chapters === null) return <p role="status" className="text-sm text-gray-500">
        {t("Loading chapters…", "正在載入章節…")}
    </p>;
    if (chapters.length === 0) return <p className="text-sm text-gray-500">
        {t("No chapters yet. A transcript and indexing are required.", "尚無章節，需要先取得字幕並完成索引。")}
    </p>;
    return <ol className="space-y-3" aria-label={t("Video chapters", "影片章節")}>
        {chapters.map((chapter) => <li key={chapter.chunk_index} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="font-medium text-gray-800">
                        {chapter.chunk_index + 1}. {chapter.title || t("Untitled chapter", "未命名章節")}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{formatTime(chapter.start_sec)}–{formatTime(chapter.end_sec)}</p>
                </div>
                <a href={`https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}&t=${Math.floor(chapter.playback_start_sec)}`}
                    target="_blank" rel="noopener noreferrer" className="text-orange-600 text-sm whitespace-nowrap">
                    {t("Play chapter", "播放章節")}
                </a>
            </div>
            <details className="mt-3 text-sm">
                <summary className="text-gray-600 cursor-pointer">{t("Transcript", "逐字稿")}</summary>
                <p className="mt-2 text-gray-600 whitespace-pre-wrap">{chapter.text || t("Transcript unavailable.", "無逐字稿內容。")}</p>
            </details>
        </li>)}
    </ol>;
}

export default VideoChapters;
